import React, { useEffect, useState, useRef } from 'react';
import api from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { toast } from 'react-toastify';
import { Download, ArrowLeft, AlertCircle, CheckCircle2, TrendingUp, Info, Target, Zap } from 'lucide-react';
import 'react-toastify/dist/ReactToastify.css';

const BCMReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState({});
  const reportRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.showSuccess) {
      toast.success('🎉 Your BCM assessment report is now available.', { autoClose: 5000 });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const getLevelLabel = (level) => {
    if (!level && level !== 0) return 'Unknown';
    if (typeof level === 'object') return `${level.level} - ${level.label}`;
    switch (level) {
      case 0: return '0 - Incomplete';
      case 1: return '1 - Initial';
      case 2: return '2 - Managed';
      case 3: return '3 - Defined';
      default: return 'Unknown';
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 0: return 'bg-gray-100 text-gray-800 border-gray-300';
      case 1: return 'bg-red-100 text-red-800 border-red-300';
      case 2: return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 3: return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Fetch BCM report using shared api instance (auto-refresh/auth handling)
  useEffect(() => {
    const run = async () => {
      try {
        const res = await api.get('maturity/bcm/report/');
        setReportData(res.data.phases || []);
      } catch (err) {
        console.error(err);
        if (err?.response?.status === 401) {
          navigate('/login');
          return;
        }
        setError('Failed to load BCM report. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [navigate]);

  const handleExportPDF = async () => {
    if (!reportRef.current) return;
    setExporting(true);

    try {
      // Temporarily expand ALL phases for PDF export
      const allPhaseIndices = {};
      reportData.forEach((_, idx) => {
        allPhaseIndices[idx] = true;
      });
      setExpandedPhases(allPhaseIndices);

      // Wait for DOM to update before capturing
      await new Promise((resolve) => setTimeout(resolve, 500));

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const startY = 25;
      const usablePageHeight = pageHeight - startY - margin;

      // Add title
      pdf.setFontSize(20);
      pdf.setTextColor(180, 83, 9);
      pdf.text('BCM Assessment Report', pageWidth / 2, 15, { align: 'center' });

      // Add date
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, 22, { align: 'center' });

      const imgWidth = pageWidth - 2 * margin;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight <= usablePageHeight) {
        pdf.addImage(imgData, 'PNG', margin, startY, imgWidth, imgHeight);
      } else {
        let yOffset = 0;
        const totalHeight = canvas.height;
        const pageHeightPx = (usablePageHeight * canvas.width) / imgWidth;

        while (yOffset < totalHeight) {
          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = Math.min(canvas.height - yOffset, pageHeightPx);
          const ctx = sliceCanvas.getContext('2d');
          ctx.drawImage(
            canvas,
            0, yOffset,
            canvas.width, sliceCanvas.height,
            0, 0,
            sliceCanvas.width, sliceCanvas.height
          );

          const pageImage = sliceCanvas.toDataURL('image/png');
          const displayHeight = (sliceCanvas.height * imgWidth) / canvas.width;

          if (yOffset > 0) pdf.addPage();
          pdf.addImage(pageImage, 'PNG', margin, startY, imgWidth, displayHeight);
          yOffset += sliceCanvas.height;
        }
      }

      // Add footer to each page
      const pageCount = pdf.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      }

      pdf.save(`bcm-report-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('✅ Report exported successfully!');

      // Collapse all phases after export
      setExpandedPhases({});
    } catch (err) {
      console.error(err);
      toast.error('❌ Failed to export report');
      // Reset expanded phases on error
      setExpandedPhases({});
    } finally {
      setExporting(false);
    }
  };

  const togglePhaseExpand = (idx) => {
    setExpandedPhases((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-amber-700 font-medium">Loading BCM Report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium mb-4">{error}</p>
          <button
            onClick={() => navigate('/bcm/dashboard')}
            className="bg-amber-600 text-white px-6 py-2 rounded hover:bg-amber-700 transition"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const chartData = reportData
    .sort((a, b) => {
      const numA = parseInt(a.code?.replace(/\D/g, '') || 0);
      const numB = parseInt(b.code?.replace(/\D/g, '') || 0);
      return numA - numB;
    })
    .map((phase) => {
      const total = phase.total_controls || 0;
      const compliant = Math.min(phase.compliant_controls || 0, total);
      const percent = total === 0 ? 0 : (compliant / total) * 100;

      return {
        phase: phase.code ? `${phase.code}` : 'Phase',
        fullName: phase.phase || 'Unknown Phase',
        compliance: parseFloat(percent.toFixed(2)),
        total,
        compliant,
      };
    });

  // Calculate overall stats
  const totalControls = reportData.reduce((sum, p) => sum + (p.total_controls || 0), 0);
  const totalCompliant = reportData.reduce((sum, p) => sum + (p.compliant_controls || 0), 0);
  const overallCompliance = totalControls === 0 ? 0 : (totalCompliant / totalControls) * 100;

  const submittedPhases = reportData.filter((p) => p.status !== 'draft').length;
  const totalGaps = reportData.reduce((sum, p) => {
    return sum + (p.focus_areas?.reduce((faSum, fa) => {
      return faSum + (fa.questions?.reduce((qSum, q) => qSum + (q.gaps?.length || 0), 0) || 0);
    }, 0) || 0);
  }, 0);

  // Calculate maturity level statistics
  const levelStats = {
    incomplete: reportData.filter((p) => p.current_level === 0).length,
    initial: reportData.filter((p) => p.current_level === 1).length,
    managed: reportData.filter((p) => p.current_level === 2).length,
    defined: reportData.filter((p) => p.current_level === 3).length,
  };

  // Calculate average target level
  const targetLevelAverage = reportData.length > 0
    ? (reportData.reduce((sum, p) => sum + (p.target_level || 0), 0) / reportData.length)
    : 0;

  // Identify high-priority gaps
  const highPriorityGaps = reportData.reduce((total, p) => {
    return total + (p.focus_areas?.reduce((faSum, fa) => {
      return faSum + (fa.questions?.reduce((qSum, q) => {
        return qSum + (q.gaps?.filter((g) => g.weight > 7)?.length || 0);
      }, 0) || 0);
    }, 0) || 0);
  }, 0);

  // Identify low-priority gaps
  const lowPriorityGaps = totalGaps - highPriorityGaps;

  // Get phases with lowest compliance
  const lowestCompliancePhases = [...chartData]
    .sort((a, b) => a.compliance - b.compliance)
    .slice(0, 3);

  // Get phases with highest compliance
  const highestCompliancePhases = [...chartData]
    .sort((a, b) => b.compliance - a.compliance)
    .slice(0, 3);

  const getHealthStatus = () => {
    if (overallCompliance >= 75) return { status: 'Healthy', color: 'bg-green-50 border-green-300', textColor: 'text-green-700', icon: 'text-green-600' };
    if (overallCompliance >= 50) return { status: 'At Risk', color: 'bg-yellow-50 border-yellow-300', textColor: 'text-yellow-700', icon: 'text-yellow-600' };
    return { status: 'Critical', color: 'bg-red-50 border-red-300', textColor: 'text-red-700', icon: 'text-red-600' };
  };

  const healthStatus = getHealthStatus();

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen py-8 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header with Navigation */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/bcm/dashboard')}
            className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium transition"
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          <h1 className="text-4xl font-bold text-gray-900">Business Continuity Maturity Assessment</h1>
          <div className="w-20"></div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl shadow border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Overall Compliance</p>
                <p className="text-3xl font-bold text-amber-600 mt-2">{overallCompliance.toFixed(1)}%</p>
              </div>
              <TrendingUp className="text-amber-600" size={32} />
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow border border-gray-200">
            <p className="text-gray-600 text-sm font-medium">Total Controls</p>
            <p className="text-3xl font-bold text-blue-600 mt-2">{totalControls}</p>
            <p className="text-sm text-green-600 mt-2">✓ {totalCompliant} Compliant</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow border border-gray-200">
            <p className="text-gray-600 text-sm font-medium">Phases Assessed</p>
            <p className="text-3xl font-bold text-purple-600 mt-2">{submittedPhases}/{reportData.length}</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow border border-gray-200">
            <p className="text-gray-600 text-sm font-medium">Total Gaps Identified</p>
            <p className="text-3xl font-bold text-red-600 mt-2">{totalGaps}</p>
          </div>
        </div>

        {/* EXECUTIVE SUMMARY SECTION */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8 border border-gray-200">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
            <div className="flex items-center gap-3">
              <Info size={28} />
              <h2 className="text-2xl font-bold">Executive Summary</h2>
            </div>
            <p className="text-blue-100 text-sm mt-2">High-level overview of your BCM maturity assessment findings</p>
          </div>

          <div className="p-8">
            {/* Health Status Banner */}
            <div className={`${healthStatus.color} border-2 rounded-lg p-6 mb-8`}>
              <div className="flex items-start gap-4">
                <div className={`${healthStatus.icon} mt-1`}>
                  <Zap size={24} />
                </div>
                <div className="flex-1">
                  <h3 className={`text-xl font-bold ${healthStatus.textColor} mb-2`}>
                    Overall Status: {healthStatus.status}
                  </h3>
                  <p className="text-gray-700">
                    Your organization's BCM maturity is currently at <span className="font-semibold">{overallCompliance.toFixed(1)}%</span> compliance.
                    {overallCompliance >= 75 && ' Your controls are well-established and operating effectively.'}
                    {overallCompliance >= 50 && overallCompliance < 75 && ' There are areas requiring attention to strengthen your BCM capabilities.'}
                    {overallCompliance < 50 && ' Immediate action is needed to improve your BCM maturity and compliance posture.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Key Findings Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Current State */}
              <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">📊</span>
                  Current Maturity State
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-white rounded border border-gray-200">
                    <span className="text-sm text-gray-700">Level 0 (Incomplete)</span>
                    <span className="font-bold text-gray-900">{levelStats.incomplete} phases</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded border border-gray-200">
                    <span className="text-sm text-gray-700">Level 1 (Initial)</span>
                    <span className="font-bold text-gray-900">{levelStats.initial} phases</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded border border-gray-200">
                    <span className="text-sm text-gray-700">Level 2 (Managed)</span>
                    <span className="font-bold text-gray-900">{levelStats.managed} phases</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded border border-gray-200">
                    <span className="text-sm text-gray-700">Level 3 (Defined)</span>
                    <span className="font-bold text-gray-900">{levelStats.defined} phases</span>
                  </div>
                </div>
              </div>

              {/* Target State */}
              <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Target size={20} className="text-amber-600" />
                  Target & Gap Analysis
                </h4>
                <div className="space-y-3">
                  <div className="p-3 bg-white rounded border border-gray-200">
                    <p className="text-sm text-gray-600 mb-1">Target Level Average</p>
                    <p className="text-2xl font-bold text-amber-600">{targetLevelAverage.toFixed(1)}</p>
                  </div>
                  <div className="p-3 bg-white rounded border border-gray-200">
                    <p className="text-sm text-gray-600 mb-1">Total Gaps Identified</p>
                    <p className="text-2xl font-bold text-red-600">{totalGaps}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 bg-red-50 rounded border border-red-200">
                      <p className="text-xs text-red-700 mb-1">High Priority</p>
                      <p className="text-xl font-bold text-red-600">{highPriorityGaps}</p>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded border border-yellow-200">
                      <p className="text-xs text-yellow-700 mb-1">Low Priority</p>
                      <p className="text-xl font-bold text-yellow-600">{lowPriorityGaps}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Insights */}
            <div className="border border-gray-200 rounded-lg p-6 bg-gray-50 mb-8">
              <h4 className="font-bold text-gray-900 mb-4 text-lg">Key Insights</h4>
              <div className="space-y-3">
                <div className="p-3 bg-white rounded border-l-4 border-l-blue-600">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Assessment Coverage:</span> {submittedPhases} out of {reportData.length} phases have been assessed ({reportData.length ? Math.round((submittedPhases / reportData.length) * 100) : 0}% complete).
                  </p>
                </div>
                {lowestCompliancePhases.length > 0 && (
                  <div className="p-3 bg-white rounded border-l-4 border-l-red-600">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Areas Needing Attention:</span> {lowestCompliancePhases.map((p) => p.phase).join(', ')} require immediate focus with compliance rates below {Math.min(...lowestCompliancePhases.map((p) => p.compliance)).toFixed(1)}%.
                    </p>
                  </div>
                )}
                {highestCompliancePhases.length > 0 && (
                  <div className="p-3 bg-white rounded border-l-4 border-l-green-600">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Strengths:</span> {highestCompliancePhases.map((p) => p.phase).join(', ')} are performing well with compliance rates above {Math.max(...highestCompliancePhases.map((p) => p.compliance)).toFixed(1)}%.
                    </p>
                  </div>
                )}
                <div className="p-3 bg-white rounded border-l-4 border-l-amber-600">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Control Distribution:</span> {totalCompliant} out of {totalControls} controls are currently compliant, with {totalControls - totalCompliant} controls requiring remediation.
                  </p>
                </div>
              </div>
            </div>

            {/* What's in This Report */}
            <div className="bg-blue-50 border border-blue-300 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-4 text-lg flex items-center gap-2">
                <Info size={20} className="text-blue-600" />
                What You'll Find in This Report
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                <div className="flex gap-3">
                  <span className="flex-shrink-0 text-blue-600 font-bold">1.</span>
                  <p><span className="font-semibold">Phase Compliance Overview Chart</span> - Visual representation of compliance rates across all assessment phases</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 text-blue-600 font-bold">2.</span>
                  <p><span className="font-semibold">Detailed Phase Breakdown</span> - Comprehensive metrics for each phase including current and target maturity levels</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 text-blue-600 font-bold">3.</span>
                  <p><span className="font-semibold">Gap Analysis</span> - Identified gaps with recommendations and priority weights for each issue</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 text-blue-600 font-bold">4.</span>
                  <p><span className="font-semibold">Compliance Metrics</span> - Detailed tables showing control counts, compliance rates, and maturity levels</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Report Section */}
        <div ref={reportRef} className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Report Header Inside */}
          <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-8 text-white">
            <h2 className="text-3xl font-bold mb-2">BCM Maturity Assessment Report</h2>
            <p className="text-amber-100">Comprehensive assessment of Business Continuity Management capabilities</p>
            <p className="text-amber-100 text-sm mt-4">Generated on {new Date().toLocaleDateString()}</p>
          </div>

          {/* Compliance Chart */}
          {chartData.length > 0 && (
            <div className="p-8 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Phase Compliance Overview</h3>
              <div className="bg-gray-50 p-6 rounded-lg">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 120 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="phase" angle={-45} textAnchor="end" height={120} interval={0} tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} label={{ value: 'Compliance %', angle: -90, position: 'insideLeft' }} />
                    <Tooltip
                      formatter={(value) => [`${value}%`, 'Compliance']}
                      labelFormatter={(label) => {
                        const data = chartData.find((d) => d.phase === label);
                        return data ? `${data.phase}: ${data.fullName}` : label;
                      }}
                      contentStyle={{
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '13px',
                      }}
                    />
                    <Bar dataKey="compliance" fill="#d97706" radius={[8, 8, 0, 0]}>
                      <LabelList dataKey="compliance" position="insideTop" formatter={(value) => `${value}%`} style={{ fill: 'white', fontSize: 12, fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Phase Legend */}
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {chartData.map((phase, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm font-bold text-amber-600">{phase.phase}</p>
                    <p className="text-xs text-gray-600 mt-1 leading-tight">{phase.fullName}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Phases Section */}
          <div className="p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Phase Details</h3>

            {reportData.map((phase, idx) => {
              const total = phase.total_controls || 0;
              const compliant = Math.min(phase.compliant_controls || 0, total);
              const compliancePercent = total === 0 ? 0 : (compliant / total) * 100;
              const isExpanded = expandedPhases[idx];
              const isDraft = phase.status === 'draft';

              return (
                <div key={idx} className="mb-6 border border-gray-200 rounded-lg overflow-hidden last:mb-0">
                  {/* Phase Header - Clickable */}
                  <button
                    onClick={() => togglePhaseExpand(idx)}
                    className="w-full bg-gray-50 hover:bg-gray-100 transition p-6 flex items-center justify-between group"
                  >
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-4 flex-wrap">
                        <h4 className="text-xl font-bold text-gray-900">
                          {phase.code ? `[${phase.code}] ` : ''}{phase.phase || 'Phase'}
                        </h4>
                        {isDraft ? (
                          <span className="px-3 py-1 bg-gray-200 text-gray-800 text-xs font-semibold rounded-full">
                            Draft
                          </span>
                        ) : (
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getLevelColor(phase.current_level)}`}>
                            {getLevelLabel(phase.current_level)}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-6 mt-4 flex-wrap">
                        <div>
                          <p className="text-sm text-gray-600">Compliance Rate</p>
                          <p className="text-lg font-bold text-amber-600">{compliancePercent.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Controls</p>
                          <p className="text-lg font-bold text-gray-900">{compliant}/{total}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex-shrink-0 ml-6">
                      <span className="px-4 py-2 bg-amber-600 group-hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition whitespace-nowrap">
                        {isExpanded ? 'Hide Details' : 'Extract Details'}
                      </span>
                    </div>
                  </button>

                  {/* Phase Content - Expandable */}
                  {isExpanded && (
                    <div className="p-6 border-t border-gray-200 bg-white">
                      {isDraft ? (
                        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg text-gray-700">
                          <AlertCircle size={20} />
                          <p className="font-medium">Assessment not submitted yet. Results will appear after submission.</p>
                        </div>
                      ) : (
                        <>
                          {/* Summary Table */}
                          <div className="overflow-x-auto mb-6">
                            <table className="w-full">
                              <thead>
                                <tr className="bg-amber-50 border-y border-amber-200">
                                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Metric</th>
                                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr className="border-b border-gray-200 hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-700">Total Controls</td>
                                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{phase.total_controls}</td>
                                </tr>
                                <tr className="border-b border-gray-200 hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-700">Compliant Controls</td>
                                  <td className="px-4 py-3 text-sm font-semibold text-green-600">{phase.compliant_controls}</td>
                                </tr>
                                <tr className="border-b border-gray-200 hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-700">Compliance Rate</td>
                                  <td className="px-4 py-3 text-sm font-semibold text-amber-600">{compliancePercent.toFixed(2)}%</td>
                                </tr>
                                <tr className="border-b border-gray-200 hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-700">Current Level</td>
                                  <td className="px-4 py-3">
                                    <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full border ${getLevelColor(phase.current_level)}`}>
                                      {getLevelLabel(phase.current_level)}
                                    </span>
                                  </td>
                                </tr>
                                <tr className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-700">Target Level</td>
                                  <td className="px-4 py-3">
                                    <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full border ${getLevelColor(phase.target_level)}`}>
                                      {getLevelLabel(phase.target_level)}
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* Focus Areas with Gaps */}
                          {phase.focus_areas && phase.focus_areas.length > 0 && (
                            <div>
                              <h5 className="font-bold text-gray-900 mb-4 text-lg">Focus Areas & Gaps</h5>
                              {phase.focus_areas
                                .filter((fa) => fa.questions?.some((q) => q.gaps?.length > 0))
                                .map((fa, fidx) => (
                                  <div key={fidx} className="mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    <h6 className="font-semibold text-gray-900 mb-4">
                                      {fa.code ? `[${fa.code}] ` : ''}{fa.title || fa.focus_area}
                                    </h6>
                                    <div className="space-y-4">
                                      {fa.questions
                                        .filter((q) => q.gaps?.length > 0)
                                        .map((q, qidx) => (
                                          <div key={qidx} className="bg-white p-4 rounded border border-gray-200">
                                            <div className="flex items-start gap-3 mb-3">
                                              <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                                              <p className="font-medium text-gray-900">
                                                {q.code ? `[${q.code}] ` : ''}{q.request}
                                              </p>
                                            </div>
                                            <div className="ml-6 space-y-3">
                                              {q.gaps.map((gap, gidx) => (
                                                <div key={gidx} className="p-3 bg-red-50 rounded border border-red-200">
                                                  <p className="text-sm font-semibold text-red-900 mb-2">Gap {gidx + 1}</p>
                                                  <p className="text-sm text-gray-700 mb-2">
                                                    <span className="font-semibold">Issue:</span> {gap.gap}
                                                  </p>
                                                  <p className="text-sm text-gray-700 mb-2">
                                                    <span className="font-semibold">Recommendation:</span> {gap.recommendation}
                                                  </p>
                                                  <p className="text-sm">
                                                    <span className="font-semibold">Priority Weight:</span>{' '}
                                                    <span
                                                      className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                                                        gap.weight > 7
                                                          ? 'bg-red-100 text-red-800'
                                                          : gap.weight > 4
                                                          ? 'bg-yellow-100 text-yellow-800'
                                                          : 'bg-green-100 text-green-800'
                                                      }`}
                                                    >
                                                      {gap.weight}/10
                                                    </span>
                                                  </p>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                ))}
                            </div>
                          )}

                          {/* No gaps message */}
                          {!phase.focus_areas?.some((fa) => fa.questions?.some((q) => q.gaps?.length > 0)) && (
                            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg text-green-700 border border-green-200">
                              <CheckCircle2 size={20} />
                              <p className="font-medium">No gaps identified. All controls are in compliance.</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Report Footer */}
          <div className="bg-gray-50 p-8 border-t border-gray-200 text-center text-sm text-gray-600">
            <p>This report was automatically generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}</p>
            <p className="mt-2">For questions or clarifications, please contact your BCM administrator.</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center sm:justify-start">
          <button
            onClick={() => navigate('/bcm/dashboard')}
            className="flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-lg font-medium transition shadow-md hover:shadow-lg"
          >
            <ArrowLeft size={18} />
            Back to Dashboard
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={18} />
            {exporting ? 'Generating PDF...' : 'Export as PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BCMReport;